'use client'
import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import {
  Network,
  Server,
  Wifi,
  AlertTriangle,
  AlertOctagon,
  CheckCircle2,
  Clock,
  Bell,
  Search,
  ChevronDown,
  ChevronRight,
  Shield,
  Camera,
  Radio,
  Router,
  Building2,
} from 'lucide-react'
import apiClient from '@/lib/noc-api-client'
import { api } from '@/lib/noc-api-client'
import { RaiseTicketModal } from '@/components/threshold-monitoring/RaiseTicketModal'
import { useLanguage } from '@/contexts/LanguageContext'

const ORG_FILTER_STORAGE_KEY = 'globalHealthDonut_selectedOrgId'

interface Organization {
  id: string
  name: string
}

const COLORS = {
  online:   '#10b981',
  alerting: '#f59e0b',
  offline:  '#ef4444',
  dormant:  '#94a3b8',
}

export interface GlobalHealthData {
  counts: { online: number; alerting: number; offline: number; dormant?: number }
  total: number
  excluded_count?: number
}

interface DeviceState {
  device_serial: string
  device_name: string | null
  device_type: string
  model: string | null
  org_id: string | null
  network_id: string | null
  network_name: string | null
  current_status: string
  went_down_at: string | null
  failure_detected: boolean
  malfunction_sign: boolean
  failure_count_last_hour: number
  ticket_raised: boolean
  team_alerted: boolean
  last_updated: string
  is_excluded?: boolean
}

interface StatusSummary {
  total_aps: number
  total_switches: number
  devices_down: number
  failures: number
  malfunction_signs: number
  devices: DeviceState[]
}

function GlobalHealthSkeleton() {
  return (
    <Card className="border border-[#DDD] shadow-none bg-gradient-to-br from-[#EEF2FF] to-white rounded-[12px]">
      <CardHeader className="pb-2 px-5 pt-5">
        <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-2" />
        <div className="h-7 w-32 bg-gray-200 rounded animate-pulse" />
      </CardHeader>
      <CardContent className="px-5 pb-5 pt-2">
        <div className="flex items-center justify-center h-[180px] w-full relative mb-4">
          <div className="w-48 h-48 rounded-full bg-gray-200 animate-pulse" />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-2">
          {[1,2,3,4].map(i => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gray-200 animate-pulse" />
              <div className="flex flex-col gap-1">
                <div className="h-4 w-8 bg-gray-200 rounded animate-pulse" />
                <div className="h-3 w-12 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function DeviceTypeIcon({ type }: { type: string }) {
  const cls = "h-4 w-4 text-[#8aa2df] flex-shrink-0"
  switch (type) {
    case 'AP':                return <Wifi className={cls} />
    case 'Switch':            return <Network className={cls} />
    case 'Security Appliance':return <Shield className={cls} />
    case 'Camera':            return <Camera className={cls} />
    case 'Sensor':            return <Radio className={cls} />
    case 'Gateway':           return <Router className={cls} />
    default:                  return <Server className={cls} />
  }
}

export function GlobalHealthDonut({
  refreshKey,
  organizations = [],
  initialData,
}: {
  refreshKey?: number
  organizations?: Organization[]
  initialData?: GlobalHealthData
}) {
  const router = useRouter()
  const { t } = useLanguage()
  // Seed state from parent-provided data so the card renders immediately.
  const [data, setData]         = useState<GlobalHealthData | null>(initialData ?? null)
  const [loading, setLoading]   = useState(!initialData)   // true only when we have NO data at all
  const [refetching, setRefetching] = useState(false)      // true during background re-fetch
  const [error, setError]       = useState<string | null>(null)

  // Sync when parent provides/updates initialData (parent may load after first render)
  useEffect(() => {
    if (initialData && !selectedOrgId) {
      setData(initialData)
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData])

  // Organisation filter — persisted in localStorage
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    return localStorage.getItem(ORG_FILTER_STORAGE_KEY) || null
  })

  const [dialogOpen, setDialogOpen]         = useState(false)
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null)
  const [modalLoading, setModalLoading]     = useState(false)
  const [modalError, setModalError]         = useState<string | null>(null)

  // All threshold devices for the selected status
  const [statusDevices, setStatusDevices]   = useState<DeviceState[]>([])
  // Active device-type tab inside modal
  const [activeTypeTab, setActiveTypeTab]   = useState<string>('All')
  // Collapsed network groups
  const [collapsed, setCollapsed]           = useState<Set<string>>(new Set())

  // Raise-ticket modal
  const [selectedDevice, setSelectedDevice] = useState<DeviceState | null>(null)
  const [showTicketModal, setShowTicketModal] = useState(false)

  /* ── persist org selection ── */
  const handleOrgChange = (orgId: string | null) => {
    setSelectedOrgId(orgId)
    if (orgId) {
      localStorage.setItem(ORG_FILTER_STORAGE_KEY, orgId)
    } else {
      localStorage.removeItem(ORG_FILTER_STORAGE_KEY)
    }
  }

  /* ── global health polling ── */
  const fetchHealth = async () => {
    try {
      // If we already have data (background refresh), use subtle spinner instead of full skeleton
      if (data) {
        setRefetching(true)
      } else {
        setLoading(true)
      }
      setError(null)
      const url = selectedOrgId
        ? `/api/dashboard/global-health?org_id=${encodeURIComponent(selectedOrgId)}`
        : '/api/dashboard/global-health'
      const d: GlobalHealthData = await apiClient(url, { cache: 'no-store' })
      setData(d)
    } catch (e: unknown) {
      setError((e as { message?: string })?.message || 'Failed to load health data')
    } finally {
      setLoading(false)
      setRefetching(false)
    }
  }

  // Keep a stable ref so the interval always calls the latest version
  const fetchHealthRef = useRef(fetchHealth)
  fetchHealthRef.current = fetchHealth

  useEffect(() => {
    // Always fetch immediately and set up a 15-second polling interval
    // so the donut stays near real-time regardless of org filter or webhook events
    fetchHealthRef.current()
    const timer = setInterval(() => fetchHealthRef.current(), 15000)
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId, refreshKey])

  /* ── open modal for a status segment ── */
  const handleSegmentClick = async (statusName: string) => {
    const status = statusName.toLowerCase()
    setSelectedStatus(status)
    setDialogOpen(true)
    setModalLoading(true)
    setModalError(null)
    setActiveTypeTab('All')
    setCollapsed(new Set())

    try {
      const [res, excludedRes]: [StatusSummary, { excluded_devices: Array<{ serial: string }> }] =
        await Promise.all([
          api.get('/api/threshold-monitoring/status'),
          api.get('/api/dashboard/excluded-devices'),
        ])

      const excludedSerials = new Set(excludedRes.excluded_devices.map(d => d.serial))

      const filtered = res.devices.filter(d => {
        const s = d.current_status.toLowerCase()
        if (status === 'offline')  return s === 'offline'  || s === 'alerting' || d.failure_detected || d.malfunction_sign
        if (status === 'alerting') return s === 'alerting'
        if (status === 'dormant')  return s === 'dormant'
        if (status === 'online')   return s === 'online'
        return s === status
      }).map(d => ({
        ...d,
        is_excluded: excludedSerials.has(d.device_serial),
      }))

      setStatusDevices(filtered)
    } catch (e: unknown) {
      setModalError((e as { message?: string })?.message || 'Failed to load devices')
      setStatusDevices([])
    } finally {
      setModalLoading(false)
    }
  }

  /* ── helpers ── */
  const toggleNetwork = (n: string) => setCollapsed(prev => {
    const next = new Set(prev)
    if (next.has(n)) { next.delete(n) } else { next.add(n) }
    return next
  })

  const getDownDuration = (went_down_at: string | null) => {
    if (!went_down_at) return null
    const mins = Math.floor((Date.now() - new Date(went_down_at).getTime()) / 60000)
    if (mins < 60) return `${mins}m`
    return `${Math.floor(mins / 60)}h ${mins % 60}m`
  }

  const getStatusBadge = (d: DeviceState) => {
    if (d.is_excluded)
      return <Badge className="bg-indigo-500 text-white text-xs">Excluded</Badge>
    if (d.malfunction_sign)
      return <Badge className="bg-purple-600 text-white text-xs flex items-center gap-1"><AlertOctagon className="h-3 w-3" />MALFUNCTION</Badge>
    if (d.failure_detected)
      return <Badge className="bg-red-600 text-white text-xs flex items-center gap-1"><AlertTriangle className="h-3 w-3" />FAILURE</Badge>
    const map: Record<string, string> = {
      online: 'bg-green-600', offline: 'bg-red-500', alerting: 'bg-orange-500', dormant: 'bg-gray-400'
    }
    const cls = map[d.current_status] || 'bg-gray-400'
    const label = d.current_status.charAt(0).toUpperCase() + d.current_status.slice(1)
    return <Badge className={`${cls} text-white text-xs`}>{label}</Badge>
  }

  /* ── device-type tabs ── */
  const deviceTypes = ['All', ...Array.from(new Set(statusDevices.map(d => d.device_type))).sort()]

  const visibleDevices = activeTypeTab === 'All'
    ? statusDevices
    : statusDevices.filter(d => d.device_type === activeTypeTab)

  /* ── group by network ── */
  const networkGroups = (() => {
    const map = new Map<string, DeviceState[]>()
    visibleDevices.forEach(d => {
      const n = d.network_name || 'Unknown Network'
      if (!map.has(n)) map.set(n, [])
      map.get(n)!.push(d)
    })
    return Array.from(map.entries()).map(([networkName, devs]) => ({
      networkName,
      devs: devs.sort((a, b) => {
        // severity first
        if (a.malfunction_sign !== b.malfunction_sign) return a.malfunction_sign ? -1 : 1
        if (a.failure_detected !== b.failure_detected) return a.failure_detected ? -1 : 1
        return (a.device_name || a.device_serial).localeCompare(b.device_name || b.device_serial)
      })
    }))
  })()

  /* ── after ticket raised, refresh ── */
  const handleTicketRaised = async () => {
    setShowTicketModal(false)
    setSelectedDevice(null)
    if (selectedStatus && selectedStatus !== 'excluded') {
      const res: StatusSummary = await api.get('/api/threshold-monitoring/status')
      const filtered = res.devices.filter(d => {
        const s = d.current_status.toLowerCase()
        if (selectedStatus === 'offline') return s === 'offline' || s === 'alerting' || d.failure_detected || d.malfunction_sign
        return s === selectedStatus
      })
      setStatusDevices(filtered)
    }
  }

  /* ── investigate → AI Manager ── */
  const handleInvestigate = (device: DeviceState) => {
    setDialogOpen(false)
    // Navigate to AI Manager with device context pre-filled via query param
    router.push(`/manager?device=${encodeURIComponent(device.device_name || device.device_serial)}&serial=${device.device_serial}&network=${encodeURIComponent(device.network_name || '')}`)
  }

  /* ── render ── */
  if (loading) return <GlobalHealthSkeleton />

  if (error || !data) {
    return (
      <Card className="border border-[#DDD] shadow-none bg-gradient-to-br from-[#EEF2FF] to-white rounded-[12px]">
        <CardHeader className="pb-2 px-5 pt-5">
          <CardTitle className="text-[18px] font-semibold text-[#313131] leading-tight mb-1">Global Network Health</CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-5 pt-2 flex items-center justify-center h-[280px]">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
              <Server className="h-8 w-8 text-red-400" />
            </div>
            <p className="text-[14px] font-medium text-[#6D6D6D]">Failed to Load</p>
            <p className="text-[12px] text-[#A4A4A4] mt-1">{error || 'Unable to fetch health data'}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const chartData = [
    { name: t.networkHealth.online,   value: data.counts.online   || 0, color: COLORS.online,   key: 'online'   },
    { name: t.networkHealth.alerting, value: data.counts.alerting  || 0, color: COLORS.alerting, key: 'alerting' },
    { name: t.networkHealth.offline,  value: data.counts.offline   || 0, color: COLORS.offline,  key: 'offline'  },
    { name: t.networkHealth.dormant,  value: data.counts.dormant   || 0, color: COLORS.dormant,  key: 'dormant'  },
  ].filter(i => i.value > 0)

  const total = data.total || 0
  const onlinePct = total > 0 ? Math.round((data.counts.online / total) * 100) : 0

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number; payload: { color: string } }> }) => {
    if (!active || !payload?.length) return null
    const d = payload[0]
    return (
      <div className="bg-white border border-[#DDD] rounded-lg shadow-lg p-3">
        <p className="text-[12px] font-semibold text-[#313131] mb-1">{d.name} Devices</p>
        <p className="text-[14px] font-bold" style={{ color: d.payload.color }}>{d.value} devices</p>
        <p className="text-[11px] text-[#6D6D6D] mt-1">{total > 0 ? ((d.value / total) * 100).toFixed(1) : 0}% of total</p>
      </div>
    )
  }

  const statusColor = selectedStatus ? (COLORS[selectedStatus as keyof typeof COLORS] ?? '#6b7280') : '#6b7280'
  const statusLabel = selectedStatus

  return (
    <>
      {/* ── Donut Card ── */}
      <Card className="border border-[#DDD] shadow-none bg-gradient-to-br from-[#EEF2FF] to-white rounded-[12px]">
        <CardHeader className="pb-2 px-5 pt-5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <CardTitle className="text-[18px] font-semibold text-[#313131] leading-tight mb-1">
                {t.networkHealth.title}
              </CardTitle>
              <p className="text-[20px] font-bold text-[#141A42] leading-tight">
                {total}
              </p>
            </div>

            {/* Organisation filter — always visible */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`flex items-center gap-1 transition-colors rounded-lg px-2.5 py-1.5 text-xs shrink-0 max-w-[150px] ${
                  selectedOrgId
                    ? 'bg-[#535EA4] text-white hover:bg-[#4a5394]'
                    : 'bg-[#f5f5f5] text-[#676767] hover:bg-[#ececec]'
                }`}>
                  <Building2 className="h-3 w-3 shrink-0" />
                  <span className="truncate">
                    {selectedOrgId
                      ? (organizations.find(o => o.id === selectedOrgId)?.name ?? 'Org')
                      : 'All Orgs'}
                  </span>
                  <ChevronDown className="h-3 w-3 shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 max-h-72 overflow-y-auto">
                <DropdownMenuItem
                  onClick={() => handleOrgChange(null)}
                  className={!selectedOrgId ? 'font-semibold text-[#535EA4]' : ''}
                >
                  All Organisations
                </DropdownMenuItem>
                {organizations.map(org => (
                  <DropdownMenuItem
                    key={org.id}
                    onClick={() => handleOrgChange(org.id)}
                    className={selectedOrgId === org.id ? 'font-semibold text-[#535EA4]' : ''}
                  >
                    {org.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent className="px-5 pb-5 pt-2">
          <div className="flex items-center justify-center h-[180px] w-full relative mb-4">
            {/* Subtle overlay spinner during background refetch */}
            {refetching && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 rounded-full pointer-events-none">
                <div className="h-6 w-6 rounded-full border-2 border-[#535EA4] border-t-transparent animate-spin" />
              </div>
            )}
            {chartData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%" cy="50%"
                      innerRadius={60} outerRadius={95}
                      paddingAngle={0} dataKey="value" strokeWidth={0}
                      onClick={d => handleSegmentClick(d.key)}
                      cursor="pointer" animationDuration={800}
                    >
                      {chartData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} className="transition-opacity hover:opacity-80" />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <div className="text-[12px] font-medium text-[#10b981] mb-0.5">{onlinePct}% {t.networkHealth.online}</div>
                    <div className="text-[28px] font-bold text-[#141A42] leading-none">{total}</div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center">
                <Server className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p className="text-[14px] text-[#6D6D6D]">No Devices Found</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 mt-2">
            {([
              ['online',   t.networkHealth.online],
              ['alerting', t.networkHealth.alerting],
              ['offline',  t.networkHealth.offline],
              ['dormant',  t.networkHealth.dormant],
            ] as [keyof typeof COLORS, string][]).map(([key, label]) => (
              <div key={key} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[key] }} />
                <div className="flex flex-col">
                  <span className="text-[12px] font-semibold leading-none mb-1" style={{ color: COLORS[key] }}>
                    {key === 'dormant' ? (data.counts.dormant || 0) : data.counts[key as 'online'|'alerting'|'offline']}
                  </span>
                  <span className="text-[12px] font-normal text-[#6D6D6D] leading-none">{label}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Excluded devices footnote */}
          {(data.excluded_count ?? 0) > 0 && (
            <p className="mt-3 text-[10px] text-[#A4A4A4] text-center">
              {data.excluded_count} device{data.excluded_count === 1 ? '' : 's'} excluded from monitoring — not counted above
            </p>
          )}
        </CardContent>
      </Card>

      {/* ── Device Details Modal ── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col gap-0 p-0 overflow-hidden">
          {/* Fixed header */}
          <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: statusColor }} />
              <span className="capitalize">{statusLabel}</span> Devices
              <span className="ml-1 text-sm font-normal text-[#6D6D6D]">— {statusDevices.length} device{statusDevices.length !== 1 ? 's' : ''}</span>
            </DialogTitle>

            {/* Device-type tabs */}
            {!modalLoading && !modalError && statusDevices.length > 0 && (
              <div className="flex gap-1 mt-3 flex-wrap">
                {deviceTypes.map(type => {
                  const count = type === 'All' ? statusDevices.length : statusDevices.filter(d => d.device_type === type).length
                  return (
                    <button
                      key={type}
                      onClick={() => { setActiveTypeTab(type); setCollapsed(new Set()) }}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                        activeTypeTab === type
                          ? 'border-[#8aa2df] bg-[#8aa2df] text-white'
                          : 'border-gray-200 text-[#676767] hover:border-[#8aa2df] hover:text-[#8aa2df] bg-white'
                      }`}
                    >
                      {type} ({count})
                    </button>
                  )
                })}
              </div>
            )}
          </DialogHeader>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {modalLoading && (
              <div className="flex items-center justify-center h-40">
                <Spinner />
              </div>
            )}

            {modalError && !modalLoading && (
              <div className="flex items-center justify-center h-40">
                <p className="text-red-500 text-sm">{modalError}</p>
              </div>
            )}

            {!modalLoading && !modalError && statusDevices.length === 0 && (
              <div className="flex items-center justify-center h-40 text-[#676767] text-sm">
                No devices found with status &quot;{selectedStatus}&quot;
              </div>
            )}

            {!modalLoading && !modalError && statusDevices.length > 0 && (
              <div className="space-y-3">
                {networkGroups.map(({ networkName, devs }) => (
                  <div key={networkName} className="border border-[#DDD] rounded-lg overflow-hidden">
                    {/* Network header */}
                    <button
                      onClick={() => toggleNetwork(networkName)}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-[#f5f7ff] hover:bg-[#eef1ff] transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {collapsed.has(networkName)
                          ? <ChevronRight className="h-4 w-4 text-[#8aa2df]" />
                          : <ChevronDown  className="h-4 w-4 text-[#8aa2df]" />
                        }
                        <span className="text-sm font-semibold text-[#313131]">{networkName}</span>
                        <Badge variant="outline" className="text-xs px-1.5">{devs.length}</Badge>
                      </div>
                      {devs.some(d => d.failure_detected || d.malfunction_sign) && (
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      )}
                    </button>

                    {/* Device rows */}
                    {!collapsed.has(networkName) && (
                      <div className="divide-y divide-[#F0F0F0]">
                        {devs.map(device => {
                          const isDown = device.current_status === 'offline' || device.current_status === 'alerting' || device.failure_detected || device.malfunction_sign
                          const canRaiseTicket = (device.failure_detected || device.malfunction_sign || device.current_status === 'offline') && !device.ticket_raised

                          return (
                            <div key={device.device_serial} className="px-4 py-3 hover:bg-[#fafafa] transition-colors">
                              <div className="flex items-start justify-between gap-3">
                                {/* Left: device info */}
                                <div className="flex items-start gap-2.5 flex-1 min-w-0">
                                  <div className="mt-0.5">
                                    <DeviceTypeIcon type={device.device_type} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    {/* Name + badges row */}
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-sm font-semibold text-[#1a1a1a] truncate">
                                        {device.device_name || device.device_serial}
                                      </span>
                                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal text-[#676767]">
                                        {device.device_type}
                                      </Badge>
                                      {device.model && (
                                        <span className="text-[11px] text-[#9a9a9a]">{device.model}</span>
                                      )}
                                      {getStatusBadge(device)}
                                      {device.team_alerted && (
                                        <Badge className="bg-blue-500 text-white text-[10px] px-1.5 flex items-center gap-1">
                                          <Bell className="h-2.5 w-2.5" />Team Alerted
                                        </Badge>
                                      )}
                                    </div>
                                    {/* Meta row */}
                                    <div className="flex items-center gap-3 mt-1 text-[11px] text-[#9a9a9a]">
                                      <span className="font-mono">{device.device_serial}</span>
                                      {device.went_down_at && (
                                        <span className="flex items-center gap-1 text-red-500">
                                          <Clock className="h-3 w-3" />
                                          Down {getDownDuration(device.went_down_at)}
                                        </span>
                                      )}
                                      {device.failure_count_last_hour > 0 && (
                                        <span className="text-orange-500">{device.failure_count_last_hour} failures/hr</span>
                                      )}
                                      {device.ticket_raised && (
                                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Ticket Raised</Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Right: action buttons */}
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {/* Investigate → AI Manager for ALL devices */}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs border-[#8aa2df] text-[#8aa2df] hover:bg-[#8aa2df] hover:text-white"
                                    onClick={() => handleInvestigate(device)}
                                  >
                                    <Search className="h-3 w-3 mr-1" />
                                    Investigate
                                  </Button>
                                  {/* Raise Alert only for down/offline devices */}
                                  {isDown && canRaiseTicket && (
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      className="h-7 text-xs"
                                      onClick={() => { setSelectedDevice(device); setShowTicketModal(true) }}
                                    >
                                      <AlertTriangle className="h-3 w-3 mr-1" />
                                      Raise Alert
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                ))}

                {/* Healthy footer */}
                {selectedStatus === 'online' && statusDevices.length > 0 && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <p className="text-sm font-medium text-green-800">All shown devices are online and healthy</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Raise Alert modal */}
      {selectedDevice && showTicketModal && (
        <RaiseTicketModal
          isOpen={showTicketModal}
          onClose={() => { setShowTicketModal(false); setSelectedDevice(null) }}
          device={selectedDevice}
          onSuccess={handleTicketRaised}
        />
      )}
    </>
  )
}
